// src/firebase.js
// Web FCM (v9 modular) with safe guards and helpers

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getMessaging,
  isSupported,
  getToken as getFcmToken,
  onMessage as onFcmMessage,
  deleteToken as deleteFcmToken,
} from 'firebase/messaging';
import { isNativeApp } from './utils/isNativeApp';

// ---- Your Firebase config (kept exactly as provided) ----
const firebaseConfig = {
  apiKey:
    process.env.REACT_APP_FIREBASE_API_KEY ||
    'AIzaSyDCERM2CxTrk9-w60XB7nYL42RiQ6hSl1s',
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    'gweffit.firebaseapp.com',
  projectId:
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'gweffit',
  storageBucket:
  process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
  'gweffit.appspot.com',
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_SENDER_ID ||
    '861803836871',
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    '1:861803836871:web:eaa22d0aeadf4db6cc15e2',
};

// Public VAPID key (env first, fallback kept)
const VAPID_KEY =
  process.env.REACT_APP_FIREBASE_VAPID_KEY ||
  'BFL5HxqRitHO7a0A4qMJoqscWf-VS7_QYp606z_lc6RDuGMWzVcRI2wbXBoNnaypdNqfYZMVB1OvksK8N_nrQu8';

// Ensure a single app instance
function ensureApp() {
  if (getApps().length) return getApp();
  return initializeApp(firebaseConfig);
}

// Resolve Messaging instance (or null if unsupported)
export const messagingPromise = (async () => {
  try {
    if (typeof window === 'undefined') return null;
    
    // Skip service worker on native Capacitor apps
    if (isNativeApp()) {
      console.log('Native platform detected - skipping web FCM');
      return null;
    }
    
    if (!('serviceWorker' in navigator)) return null;

    const supported = await isSupported().catch(() => false);
    if (!supported) return null;

    const app = ensureApp();

    // Register SW for background notifications (no-op if already registered)
    try {
      await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    } catch (swErr) {
      console.warn('FCM service worker registration warning:', swErr);
    }

    return getMessaging(app);
  } catch (e) {
    console.warn('messagingPromise init failed:', e);
    return null;
  }
})();

// Get a push token
export async function getToken(messaging) {
  try {
    if (!messaging) return null;
    
    // Skip on native platforms
    if (isNativeApp()) return null;
    
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker) return null;

    // Use the same SW you registered (register if absent)
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
    }

    const token = await getFcmToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: reg
    });
    return token || null;
  } catch (e) {
    console.error('getToken error:', e);
    return null;
  }
}

// Foreground message handler passthrough
export const onMessage = onFcmMessage;

// Delete current push token safely
export async function deleteTokenSafely() {
  try {
    const messaging = await messagingPromise;
    if (!messaging) return false;
    const deleted = await deleteFcmToken(messaging).catch((e) => {
      console.warn('deleteToken (modular) failed:', e);
      return false;
    });
    return !!deleted;
  } catch (e) {
    console.error('deleteTokenSafely error:', e);
    return false;
  }
}

// Keep your existing imports working:
// If other files import { deleteToken } from './firebase',
// this alias will match that name.
export { deleteTokenSafely as deleteToken, ensureApp };
